import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {UrlService} from "app/core/services/url.service";
import {ErrorHandlingService} from "app/core/services/error.handling.service";
import {PermissionsService} from "app/core/services/permissions.service";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {UrlEnum} from "app/core/types/url-enum";
import {ObservableErrHandler} from "app/core/types/err.handling.types";
import { AppResource, ResourceType} from "./feat-mgmt.types";
import {StockRating} from "../../../sterilization/sterilization.types";

@Injectable({providedIn: 'root'})
export class AppResourceService
{
    private readonly _urlService: UrlService = inject(UrlService);
    private readonly _errHandlService: ErrorHandlingService = inject(ErrorHandlingService);
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _permissionSrv: PermissionsService = inject(PermissionsService);
    private readonly _resourceData: BehaviorSubject<AppResource[]> = new BehaviorSubject([]);
    private readonly _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);

    get busy$() {
        return this._busy.asObservable();
    }

    /**
     * Constructor
     */
    constructor() {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<AppResource[]> {
        return this._resourceData.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get data
     */
    getResourceInfoFromBE(): void
    {
        if(!this._permissionSrv.isTenantAdmin() && !this._permissionSrv.isTenantOwner()){
            return;
        }

        if(this._resourceData.value && this._resourceData.value.length > 0){
            //do not get new data if data exists
            return;
        }

        this._busy.next(true);
        const obs = this._httpClient.get<AppResource[]>(
            this._urlService.getUrl(UrlEnum.private_get_app_resource));

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    this._resourceData.next(value);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));

        errWrapper.observable.subscribe();
    }

    getAvailableWorkUnits(){
        if(this._resourceData.value){
            const workUnitres: AppResource = this._resourceData.value.find(appRes => appRes.resourceType == ResourceType.WORK_UNIT)
            if(workUnitres){
                return workUnitres.availableResourceCount;
            }
        }
        return 1;
    }

    public getStockRating(res:AppResource): StockRating
    {
        switch (res.resourceType){
            case ResourceType.AI_TOKENS:
                return StockRating.GOOD;
            case ResourceType.WORK_UNIT:
                return StockRating.GOOD;
            case ResourceType.STORAGE:
                return StockRating.GOOD;
            case ResourceType.SMS:
                if(res.availableResourceCount < 100){
                    return StockRating.LOW;
                }
                if(res.availableResourceCount >= 100 && res.availableResourceCount < 300){
                    return StockRating.GOOD;
                }
                if(res.availableResourceCount >= 300){
                    return StockRating.HI;
                }
            case ResourceType.EMAIL:
                return StockRating.GOOD;
        }
    }

    public getResourcesThatAreLow():AppResource[]{
        if(this._resourceData?.value?.length > 0){
            const resources:AppResource[] = this._resourceData.value.concat([]);
            resources.sort((a,b) => b.availableResourceCount - a.availableResourceCount );
            return resources.filter(res => this.getStockRating(res) == StockRating.LOW);
        }
        return [];
    }
}
